import React, { useEffect, useState } from "react";
import background from "../images/aboutimages/background.png";
import phone from "../images/aboutimages/phone.png";
import mail from "../images/aboutimages/mail.png";
import social from "../images/aboutimages/social.png";
import facebookImg from "../images/aboutimages/facebook.png";
import instagramImg from "../images/aboutimages/instagram.png";
import youtubeImg from "../images/aboutimages/youtube.png";
import backgroundImage from "../images/aboutimages/bgvector.png";
import { toast } from "react-hot-toast";
import { Helmet } from "react-helmet";
import { api_url } from "../config/config";

const ContactPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    email: "",
    phone: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  /* ── validation ── */
  const validateField = (name, value) => {
    if (name === "email") {
      if (!value) return "Email is required";
      if (
        !/^[a-zA-Z0-9._%+-]+@(?=[A-Za-z0-9]*[A-Za-z])[A-Za-z0-9]+(?:\.[A-Za-z0-9]+)*\.[A-Za-z]{2,}$/.test(
          value,
        )
      )
        return "Enter a valid email address";
    }
    if (name === "phone") {
      if (!value) return "Phone number is required";
      if (!/^\d{10}$/.test(value)) return "Enter a valid 10-digit phone number";
    }
    if (name === "message") {
      if (!value) return "Message cannot be empty";
      if (value.length < 10) return "Message must be at least 10 characters";
    }
    return "";
  };

  const validateForm = () => {
    const newErrors = {};
    ["email", "phone", "message"].forEach((k) => {
      const e = validateField(k, formData[k]);
      if (e) newErrors[k] = e;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
    setErrors((p) => ({ ...p, [name]: validateField(name, value) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const res = await fetch(`${api_url}/contactus`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email,
          mobile_number: formData.phone,
          message: formData.message,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Message sent successfully!");
        setFormData({ email: "", phone: "", message: "" });
        setErrors({});
      } else {
        throw new Error(data.message || "Failed to submit form");
      }
    } catch (err) {
      toast.error(err.message || "Failed to submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ─── shared input class ─── */
  const inputCls = (field) =>
    `w-full h-11 px-4 bg-white border-[1.5px] rounded-xl font-poppins text-[13px] text-[#1a2625] placeholder-[#8faba9] outline-none transition-all duration-200 ${
      errors[field]
        ? "border-red-400 focus:border-red-400 focus:shadow-[0_0_0_3px_rgba(239,68,68,0.12)]"
        : "border-[#e2eaea] focus:border-[#1e4a46] focus:shadow-[0_0_0_3px_rgba(95,189,179,0.15)]"
    }`;

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 sm:px-8 py-12 bg-cover bg-center font-poppins"
      style={{ backgroundImage: `url(${background})` }}
    >
      <Helmet>
        <title>Contact Us | MediSYS Pharmacy</title>
        <meta
          name="description"
          content="Contact MediSYS Pharmacy. Buy medicines online and experience fastest home delivery."
        />
        <meta
          name="keywords"
          content="pharmacy, medical stores, online pharmacy, online medical store hyderabad"
        />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Contact Us | MediSYS Pharmacy" />
        <meta
          property="og:description"
          content="Reach out to MediSYS Pharmacy for any queries."
        />
      </Helmet>

      {/* ── Page card wrapper ── */}
      <div className="w-full max-w-[960px] mx-auto">
        {/* ── Page header ── */}
        <div className="text-center mb-10">
          <div className="flex items-center justify-center gap-3 mb-2">
            <span className="w-8 h-[2px] bg-[#ff601e] rounded-full" />
            <span className="text-[11px] font-semibold uppercase tracking-widest text-[#ff601e]">
              We're here to help
            </span>
            <span className="w-8 h-[2px] bg-[#ff601e] rounded-full" />
          </div>
          <h1 className="font-bold text-[28px] sm:text-[36px] text-[#1e4a46] leading-tight">
            Contact Us
          </h1>
          <p className="mt-2 text-[13px] text-[#607d7b] max-w-[360px] mx-auto">
            Have a question or need help? Send us a message and we'll get back
            to you shortly.
          </p>
        </div>

        {/* ── Two columns ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* ═══════════════════════════════
              LEFT — Contact Form
          ═══════════════════════════════ */}
          <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(22,44,42,0.12)] p-6 sm:p-8 border border-[#e2eaea]">
            {/* Card header strip */}
            <div className="w-full h-[3px] bg-gradient-to-r from-[#162c2a] via-[#5fbdb3] to-[#ff601e] rounded-full mb-6" />

            <h2 className="font-bold text-[18px] text-[#1e4a46] mb-1">
              Send a Message
            </h2>
            <p className="text-[12px] text-[#8faba9] mb-6">
              Fill in the form below and we'll respond within 24 hours.
            </p>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Email */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-semibold text-[#374747]">
                  Email Address <span className="text-[#ff601e]">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#8faba9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                      <polyline points="22,6 12,13 2,6" />
                    </svg>
                  </span>
                  <input
                    type="text"
                    name="email"
                    placeholder="yourname@email.com"
                    value={formData.email}
                    onChange={handleChange}
                    className={`${inputCls("email")} pl-10`}
                  />
                </div>
                {errors.email && (
                  <p className="text-red-500 text-[11px] flex items-center gap-1">
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.email}
                  </p>
                )}
              </div>

              {/* Phone */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-semibold text-[#374747]">
                  Mobile Number <span className="text-[#ff601e]">*</span>
                </label>
                <div
                  className="flex items-center border-[1.5px] rounded-xl overflow-hidden transition-all duration-200 focus-within:border-[#1e4a46] focus-within:shadow-[0_0_0_3px_rgba(95,189,179,0.15)]"
                  style={{ borderColor: errors.phone ? "#f87171" : "#e2eaea" }}
                >
                  <div className="flex items-center gap-1.5 px-3 bg-[#f8fafa] border-r border-[#e2eaea] h-11 flex-shrink-0">
                    <span className="text-[14px]">🇮🇳</span>
                    <span className="text-[13px] font-semibold text-[#374747]">
                      +91
                    </span>
                  </div>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="10-digit mobile number"
                    value={formData.phone}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                    className="flex-1 h-11 px-3 bg-transparent outline-none text-[13px] text-[#1a2625] placeholder-[#8faba9]"
                  />
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-[11px] flex items-center gap-1">
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {errors.phone}
                  </p>
                )}
              </div>

              {/* Message */}
              <div className="flex flex-col gap-1">
                <label className="text-[12px] font-semibold text-[#374747]">
                  Message <span className="text-[#ff601e]">*</span>
                </label>
                <textarea
                  name="message"
                  placeholder="Write your message here… (min. 10 characters)"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className={`${inputCls("message")} h-auto py-3 resize-none`}
                />
                <div className="flex items-center justify-between">
                  {errors.message ? (
                    <p className="text-red-500 text-[11px] flex items-center gap-1">
                      <svg
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="12" y1="8" x2="12" y2="12" />
                        <line x1="12" y1="16" x2="12.01" y2="16" />
                      </svg>
                      {errors.message}
                    </p>
                  ) : (
                    <span />
                  )}
                  <span className="text-[10px] text-[#8faba9]">
                    {formData.message.length} / 500
                  </span>
                </div>
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-[#162c2a] hover:bg-[#1e4a46] disabled:bg-[#8faba9] text-white font-semibold text-[14px] rounded-xl transition-all duration-200 shadow-[0_2px_10px_rgba(22,44,42,0.25)] hover:shadow-[0_4px_16px_rgba(22,44,42,0.35)] hover:-translate-y-px flex items-center justify-center gap-2 mt-1"
              >
                {loading ? (
                  <>
                    <svg
                      className="animate-spin w-4 h-4"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="white"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="white"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    Sending…
                  </>
                ) : (
                  <>
                    Send Message
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </>
                )}
              </button>
            </form>
          </div>

          {/* ═══════════════════════════════
              RIGHT — Contact Info
          ═══════════════════════════════ */}
          <div className="flex flex-col gap-4">
            {/* Email */}
            <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_4px_18px_rgba(22,44,42,0.08)] p-5 flex items-center gap-4 hover:shadow-[0_6px_24px_rgba(22,44,42,0.14)] hover:-translate-y-0.5 transition-all duration-200 group">
              <div className="w-12 h-12 rounded-xl bg-[#162c2a] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1e4a46] transition-colors">
                <img
                  src={mail}
                  alt="Email"
                  className="w-5 h-5 object-contain"
                />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#8faba9] mb-0.5">
                  Email Us On
                </p>
                <a
                  href="mailto:medisys.customercare@gmail.com"
                  className="text-[14px] font-semibold text-[#1e4a46] hover:text-[#ff601e] transition-colors"
                >
                  medisys.customercare@gmail.com
                </a>
              </div>
            </div>

            {/* Phone */}
            <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_4px_18px_rgba(22,44,42,0.08)] p-5 flex items-center gap-4 hover:shadow-[0_6px_24px_rgba(22,44,42,0.14)] hover:-translate-y-0.5 transition-all duration-200 group">
              <div className="w-12 h-12 rounded-xl bg-[#162c2a] flex items-center justify-center flex-shrink-0 group-hover:bg-[#1e4a46] transition-colors">
                <img
                  src={phone}
                  alt="Phone"
                  className="w-5 h-5 object-contain"
                />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#8faba9] mb-0.5">
                  Customer Support
                </p>
                <a
                  href="tel:+917799923501"
                  className="text-[14px] font-semibold text-[#1e4a46] hover:text-[#ff601e] transition-colors"
                >
                  +91 7799923501
                </a>
              </div>
            </div>

            {/* Social */}
            <div className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_4px_18px_rgba(22,44,42,0.08)] p-5 flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-[#162c2a] flex items-center justify-center flex-shrink-0">
                <img
                  src={social}
                  alt="Social"
                  className="w-5 h-5 object-contain"
                />
              </div>
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-widest text-[#8faba9] mb-2">
                  Follow Us
                </p>
                <div className="flex items-center gap-3">
                  <a
                    href="https://www.facebook.com/p/Medisys-Pharmacy-61553928654627/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-lg border border-[#e2eaea] flex items-center justify-center hover:bg-[#ff601e] hover:border-[#ff601e] transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <img
                      src={facebookImg}
                      alt="Facebook"
                      className="w-4 h-4 object-contain"
                    />
                  </a>
                  <a
                    href="https://www.instagram.com/medisys_pharmacy?utm_source=qr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-lg border border-[#e2eaea] flex items-center justify-center hover:bg-[#ff601e] hover:border-[#ff601e] transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <img
                      src={instagramImg}
                      alt="Instagram"
                      className="w-4 h-4 object-contain"
                    />
                  </a>
                  <a
                    href="https://www.youtube.com/@MediSYSPharmacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-lg border border-[#e2eaea] flex items-center justify-center hover:bg-[#ff601e] hover:border-[#ff601e] transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <img
                      src={youtubeImg}
                      alt="YouTube"
                      className="w-4 h-4 object-contain"
                    />
                  </a>
                </div>
              </div>
            </div>

            {/* Map placeholder / CTA card */}
            <div className="bg-[#162c2a] rounded-2xl p-6 relative overflow-hidden">
              {/* Plus pattern */}
              <div
                className="absolute inset-0 opacity-[0.05] pointer-events-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "repeat",
                  backgroundSize: "40px 40px",
                }}
              />
              <div className="relative">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-[2px] bg-[#ff601e] rounded-full" />
                  <span className="text-[10px] font-semibold uppercase tracking-widest text-[#5fbdb3]">
                    Head Office
                  </span>
                </div>
                <p className="font-bold text-[16px] text-white mb-1">
                  MediSYS Pharmacy
                </p>
                <p className="text-[12px] text-white/55 leading-relaxed mb-4">
                  Panjagutta, Hyderabad
                  <br />
                  Telangana – 500082
                </p>
                <a
                  href="https://g.co/kgs/4ETNhU7"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#ff601e] hover:bg-[#e55519] text-white font-semibold text-[12px] px-4 h-9 rounded-xl transition-all duration-200 shadow-[0_3px_10px_rgba(255,96,30,0.35)]"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                  View on Maps
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
